/* Mocked in-memory implementation of NewsClient for local development and UI wiring. */

import { INewsClient } from './INewsClient';
import {
  NewsItemDto,
  NewsCommentDto,
  NewsTagDto,
  NewsEngagementSummaryDto,
  CreateUpdateNewsItemDto,
  CreateUpdateNewsTagDto,
  NewsItemStatus,
  getNewsItemStatus,
} from './newsTypes';

/* ---- Date helpers (keeps mock data evergreen) ---- */

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
}

/* ---- Mock Data ---- */

let mockNewsItems: NewsItemDto[] = [
  {
    id: 1,
    title: 'New Grab & Go Range - Fresh Food to Go',
    content: `<p>We're thrilled to launch our enhanced Grab & Go range, designed to capture busy customers looking for quick, quality food options.</p>
<p><strong>New Products Include:</strong></p>
<ul>
<li>Premium Sandwiches & Wraps - ready-made fresh daily</li>
<li>Hot Food Counter - sausage rolls, pasties, and pizza slices</li>
<li>Fresh Fruit Pots & Healthy Snacks</li>
<li>Chilled Coffee & Energy Drinks</li>
</ul>
<p><strong>Merchandising Tips:</strong></p>
<ul>
<li>Place hot food counter near the till for impulse purchases</li>
<li>Ensure grab & go fridges are restocked every 2 hours during peak times</li>
<li>Display meal deals prominently (sandwich + drink + snack for £3.50)</li>
</ul>
<p>Training materials and planograms are available in the Documents section.</p>`,
    summary: 'Launch of new Grab & Go food-to-go range targeting busy commuters and lunchtime trade',
    imageUrl: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(9),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(14),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: daysAgo(10),
    updatedByUserId: 1,
    updatedByUserName: 'Sarah Johnson',
    viewCount: 342,
    likeCount: 89,
    commentCount: 23,
    tags: [2, 12, 3],  // menu, update, promotion
  },
  {
    id: 2,
    title: 'Age Verification Compliance - Challenge 25 Reminder',
    content: `<p>Following recent test purchases, we're reinforcing our Challenge 25 policy across all stores. All colleagues MUST check ID for customers who appear under 25 when purchasing:</p>
<p><strong>Restricted Products:</strong></p>
<ul>
<li>All alcohol products</li>
<li>Tobacco and vaping products</li>
<li>Energy drinks (to under 16s)</li>
<li>Knives and blades</li>
<li>Aerosols and solvents</li>
</ul>
<p><strong>Key Reminders:</strong></p>
<ol>
<li>Use the POS age verification prompt - NEVER bypass it</li>
<li>Accept only valid photo ID: passport, driving licence, PASS card</li>
<li>Refuse the sale if in any doubt</li>
<li>Complete refusal register immediately</li>
<li>Display Challenge 25 signage at all tills and on restricted product displays</li>
</ol>
<p>Store managers: review refusals register weekly. Contact your area manager if you have questions.</p>`,
    summary: 'Critical reminder on age verification and Challenge 25 compliance for all restricted products',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(11),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(12),
    createdByUserId: 2,
    createdByUserName: 'Mike Williams',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 567,
    likeCount: 45,
    commentCount: 12,
    tags: [6, 5, 14],  // health-safety, compliance, important
  },
  {
    id: 3,
    title: 'Employee of the Month - November 2024',
    content: `<p>Congratulations to <strong>Emma Thompson</strong> from Store 005 for being named Employee of the Month!</p>
<p>Emma has been outstanding in managing peak morning trade, consistently achieving the fastest transaction times while maintaining excellent customer service. She's also taken initiative to reorganize the confectionery displays, resulting in a 15% increase in impulse purchases.</p>
<p>Emma's proactive approach to merchandising and her dedication to colleague training make her a true asset to our convenience store team.</p>
<p>Thank you, Emma, for setting the standard!</p>`,
    summary: 'Celebrating Emma Thompson from Store 005 as our November Employee of the Month',
    imageUrl: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(28),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(29),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 289,
    likeCount: 156,
    commentCount: 34,
    tags: [7],  // recognition
  },
  {
    id: 4,
    title: 'Store 001 - Lottery & Paypoint Promotion Week',
    content: `<p>Store 001 will be running an intensive Lottery & Paypoint promotion week starting Monday to drive footfall and increase basket spend.</p>
<p><strong>Promotion Details:</strong></p>
<ul>
<li>Scratch card multibuy offers - 3 for £5</li>
<li>Free Paypoint promotional items for bill payment customers</li>
<li>Extra entries into our weekly customer prize draw</li>
<li>Counter display featuring new lottery games</li>
</ul>
<p><strong>Store Team Actions:</strong></p>
<ul>
<li>Actively promote services at checkout</li>
<li>Ensure till points are stocked with promotional materials</li>
<li>Upsell complementary products (drinks, snacks) to lottery customers</li>
</ul>
<p>Expected increase in counter transactions - all team members rostered for additional coverage during peak times (7-9am, 12-2pm, 5-7pm).</p>`,
    summary: 'Lottery & Paypoint promotion week at Store 001 to drive transactions and basket spend',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: ['001'],
    isGlobal: false,
    isPinned: false,
    publishedAtUtc: daysAgo(7),
    expiresAtUtc: daysFromNow(21),
    createdAtUtc: daysAgo(8),
    createdByUserId: 3,
    createdByUserName: 'David Chen',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 45,
    likeCount: 12,
    commentCount: 5,
    tags: [3, 11],  // promotion, event
  },
  {
    id: 5,
    title: 'System Maintenance Notice',
    content: `<p>Scheduled system maintenance will occur on Sunday, December 1st from 02:00 to 06:00 GMT.</p>
<p>During this time, the following services may be unavailable:</p>
<ul>
<li>Order management system</li>
<li>Stock count functionality</li>
<li>Reporting dashboard</li>
</ul>
<p>Please ensure any critical operations are completed before the maintenance window.</p>`,
    summary: 'System maintenance scheduled for December 1st, 2025',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: null,
    expiresAtUtc: daysFromNow(3),
    createdAtUtc: daysAgo(5),
    createdByUserId: 4,
    createdByUserName: 'Tech Support',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 0,
    likeCount: 0,
    commentCount: 0,
    tags: [8, 12],  // maintenance, update
  },
  {
    id: 6,
    title: 'Christmas Trading Hours & Peak Season Preparation',
    content: `<p>Confirmed trading hours for the Christmas period and key reminders for our busiest trading weeks:</p>
<p><strong>Trading Hours:</strong></p>
<p><strong>December 24th (Christmas Eve):</strong> 06:00 - 20:00 (extended hours for last-minute shoppers)</p>
<p><strong>December 25th (Christmas Day):</strong> CLOSED</p>
<p><strong>December 26th (Boxing Day):</strong> 08:00 - 20:00</p>
<p><strong>December 31st (New Year's Eve):</strong> 06:00 - 22:00</p>
<p><strong>January 1st (New Year's Day):</strong> 08:00 - 20:00</p>
<p><strong>Peak Season Priorities:</strong></p>
<ul>
<li>Keep all core lines fully stocked: bread, milk, soft drinks, confectionery, tobacco</li>
<li>Ensure chiller and freezer doors are functioning - call maintenance immediately if issues arise</li>
<li>ATM cash levels monitored daily - customer demand is 3x normal during this period</li>
<li>Hot food counter to be staffed continuously during open hours</li>
<li>Additional security presence on site December 23-26</li>
</ul>
<p>Store managers: review rotas to ensure adequate coverage for extended hours and peak trade periods.</p>`,
    summary: 'Christmas trading hours and essential preparation checklist for peak convenience store trading period',
    imageUrl: 'https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(14),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(15),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 423,
    likeCount: 67,
    commentCount: 8,
    tags: [9, 13],  // holiday, reminder
  },
  // ---- Vapiano News Items (franchiseId: 10) ----
  {
    id: 7,
    title: 'New Summer Pasta Specials: Gamberoni & Primavera',
    content: `<p>We're excited to announce two new seasonal pasta dishes launching across all Vapiano UK restaurants this week.</p>
<p><strong>Spaghetti Gamberoni</strong> — king prawns sautéed with garlic, cherry tomatoes, and a hint of chilli in white wine sauce, finished with fresh parsley.</p>
<p><strong>Penne Primavera</strong> — seasonal vegetables including courgette, asparagus, and sun-dried tomatoes tossed in a light pesto cream sauce.</p>
<p><strong>Key Actions:</strong></p>
<ul>
<li>Pasta station teams: review the new recipe cards in Documents</li>
<li>Ensure all new ingredients are added to your daily prep lists</li>
<li>Update menu boards at each station</li>
<li>Upsell opportunity: pair with a glass of house white wine or Aperol Spritz</li>
</ul>
<p>Chi va piano, va sano e va lontano!</p>`,
    summary: 'Two new seasonal pasta specials launching — Gamberoni & Primavera — with pairing recommendations',
    imageUrl: 'https://images.unsplash.com/photo-1611270629569-8b357cb88da9?q=80&w=1287&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(3),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(5),
    createdByUserId: 10,
    createdByUserName: 'Marco Bianchi',
    updatedAtUtc: daysAgo(3),
    updatedByUserId: 10,
    updatedByUserName: 'Marco Bianchi',
    viewCount: 187,
    likeCount: 64,
    commentCount: 12,
    tags: [15, 16, 20],  // fresh-pasta, italian-cuisine, menu
  },
  {
    id: 8,
    title: 'Open Kitchen Hygiene & Herb Garden Maintenance Standards',
    content: `<p>As a brand built on transparency and freshness, our open kitchens and signature herb gardens are core to the Vapiano experience. This update reinforces our standards.</p>
<p><strong>Open Kitchen Standards:</strong></p>
<ul>
<li>All visible prep surfaces must be wiped down every 30 minutes during service</li>
<li>Pasta rollers and cutters cleaned between each dough batch</li>
<li>Pizza station flour areas swept hourly</li>
<li>Staff uniforms must be spotless — apron changes every 4 hours minimum</li>
<li>Guests can see everything — maintain awareness at all times</li>
</ul>
<p><strong>Herb Garden Care:</strong></p>
<ul>
<li>Basil plants watered Mon/Wed/Fri mornings before service (7:00am)</li>
<li>Rosemary trimmed weekly — use cuttings for pizza and focaccia</li>
<li>Olive tree inspection monthly — report any leaf discolouration to facilities</li>
<li>Replace wilting plants immediately — contact the approved nursery supplier</li>
</ul>
<p>Remember: our herb gardens and olive trees are what make Vapiano unique. They are not decoration — they are part of our food.</p>`,
    summary: 'Updated hygiene standards for open kitchens and care procedures for our signature herb gardens and olive trees',
    imageUrl: 'https://plus.unsplash.com/premium_photo-1733317481338-2cffa5ac3413?q=80&w=1171&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(10),
    expiresAtUtc: daysFromNow(180),
    createdAtUtc: daysAgo(12),
    createdByUserId: 11,
    createdByUserName: 'Giulia Romano',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 234,
    likeCount: 41,
    commentCount: 7,
    tags: [17, 18, 5],  // open-kitchen, herb-garden, compliance
  },
  {
    id: 9,
    title: 'Great Portland Street: New Chip Card System Upgrade',
    content: `<p>Our flagship Great Portland Street location is piloting the upgraded chip card system starting next Monday.</p>
<p><strong>What's changing:</strong></p>
<ul>
<li>New NFC-enabled chip cards with contactless tap functionality</li>
<li>Faster scanning at all pasta, pizza, and bar stations</li>
<li>Integration with the Vapiano loyalty app for automatic point collection</li>
<li>Improved end-of-visit payment experience — tap to pay at exit</li>
</ul>
<p><strong>VAP001 Team Actions:</strong></p>
<ul>
<li>Attend the 30-minute training session (see task schedule)</li>
<li>Familiarise yourselves with the new card readers at each station</li>
<li>Be prepared to guide customers through the new process during week 1</li>
<li>Report any scanning issues immediately to shift manager</li>
</ul>
<p>If the pilot is successful, we'll roll out to Bankside and Manchester within 4 weeks.</p>`,
    summary: 'Great Portland Street piloting new NFC chip card system — training required for all VAP001 staff',
    imageUrl: 'https://images.unsplash.com/photo-1683313041281-c2fa5f195608?q=80&w=1331&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 10,
    storeNumbers: ['VAP001'],
    isGlobal: false,
    isPinned: false,
    publishedAtUtc: daysAgo(2),
    expiresAtUtc: daysFromNow(30),
    createdAtUtc: daysAgo(4),
    createdByUserId: 12,
    createdByUserName: 'James Fletcher',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 52,
    likeCount: 18,
    commentCount: 4,
    tags: [12, 4],  // update, training
  },
  {
    id: 10,
    title: 'Fresh Pasta Masterclass: Carbonara Technique Workshop',
    content: `<p>All pasta station staff are invited to an in-store training workshop focusing on perfecting our Carbonara technique — consistently our #1 selling pasta dish.</p>
<p><strong>Workshop Focus:</strong></p>
<ul>
<li>Egg yolk and Pecorino Romano emulsion — the correct temperature to avoid scrambling</li>
<li>Guanciale rendering — achieving the perfect crisp without burning</li>
<li>Pasta water starch content — why it matters for sauce adhesion</li>
<li>Timing from pan to plate — Carbonara waits for no one</li>
<li>Common mistakes and customer complaints</li>
</ul>
<p><strong>Schedule:</strong></p>
<p>Sessions run 30 minutes before opening on Tuesday/Wednesday next week. Check your store's task board for your assigned time slot.</p>
<p>Led by Head Chef Marco Bianchi (video link for Manchester team).</p>`,
    summary: 'In-store Carbonara technique workshop for all pasta station staff — perfecting our best-seller',
    imageUrl: 'https://images.unsplash.com/photo-1615502731897-55bd83d3b14b?q=80&w=1288&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(5),
    expiresAtUtc: daysFromNow(14),
    createdAtUtc: daysAgo(7),
    createdByUserId: 10,
    createdByUserName: 'Marco Bianchi',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 156,
    likeCount: 73,
    commentCount: 9,
    tags: [4, 15, 16],  // training, fresh-pasta, italian-cuisine
  },
  {
    id: 11,
    title: 'Aperol Spritz Summer Promotion: Bar Menu Launch',
    content: `<p>Summer is here and so is our Aperol Spritz promotion! Vapiano is "Pasta | Pizza | <strong>Bar</strong>" — let's make sure customers know it.</p>
<p><strong>Promotion Details:</strong></p>
<ul>
<li>Aperol Spritz at £7.95 (down from £9.50) — available all day</li>
<li>New Summer Spritz Board: Aperol, Limoncello Spritz, Hugo Spritz</li>
<li>Happy Hour 4-7pm: 2-for-1 on all spritz drinks</li>
<li>Instagram-worthy glassware — branded Aperol balloon glasses</li>
</ul>
<p><strong>Bar Team Actions:</strong></p>
<ul>
<li>Ensure Aperol, prosecco, and soda water stocks are doubled for the period</li>
<li>Orange garnish prep: slice fresh oranges every 2 hours (not pre-sliced the night before)</li>
<li>Display summer bar menu at entrance and on all tables</li>
<li>Suggest pairing with Pizza Margherita or Bruschetta Classica</li>
</ul>
<p>Promotion runs for 8 weeks. Let's make this our biggest bar revenue summer yet.</p>`,
    summary: 'Summer Aperol Spritz promotion launching — happy hour pricing and new Spritz Board for the bar',
    imageUrl: 'https://images.unsplash.com/photo-1570598912132-0ba1dc952b7d?q=80&w=1287&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(1),
    expiresAtUtc: daysFromNow(56),
    createdAtUtc: daysAgo(3),
    createdByUserId: 11,
    createdByUserName: 'Giulia Romano',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 98,
    likeCount: 55,
    commentCount: 6,
    tags: [3, 19],  // promotion, bar-drinks
  },
  // ---- Five Guys News Items (franchiseId: 11) ----
  {
    id: 12,
    title: 'Limited Time: Coffee Milkshake Mix-In with Jimmy\'s Iced Coffee',
    content: `<p>Big news! Our latest limited-time milkshake mix-in is here — <strong>Iced Coffee made with Jimmy's</strong>.</p>
<p>This smooth and indulgent coffee flavour pairs perfectly with our vanilla base and can be combined with any of our other 11 mix-ins for a custom creation.</p>
<p><strong>Crew Actions:</strong></p>
<ul>
<li>Ensure Jimmy's Iced Coffee concentrate is stocked and chilled at the shake station</li>
<li>Recommended pairings to suggest to customers: Coffee + Chocolate, Coffee + Salted Caramel, Coffee + Lotus Biscoff</li>
<li>Update the LTO (Limited Time Offer) board above the shake station</li>
<li>This is a great upsell opportunity — ask every burger customer if they'd like to try it!</li>
</ul>
<p><strong>Allergen Note:</strong> Contains milk. May contain traces of peanuts (shared environment). Ensure allergen signage is displayed.</p>
<p>LTO runs while stocks last. Push it hard!</p>`,
    summary: 'New limited-time milkshake mix-in — Jimmy\'s Iced Coffee — now available at all UK stores',
    imageUrl: 'https://images.unsplash.com/photo-1572490122747-3968b75cc699?q=80&w=1287&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(4),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(6),
    createdByUserId: 20,
    createdByUserName: 'Ryan Mitchell',
    updatedAtUtc: daysAgo(4),
    updatedByUserId: 20,
    updatedByUserName: 'Ryan Mitchell',
    viewCount: 312,
    likeCount: 89,
    commentCount: 15,
    tags: [21, 23, 3],  // burgers-grill, milkshakes, promotion
  },
  {
    id: 13,
    title: 'Peanut Allergen Awareness: Updated Signage Requirements',
    content: `<p>Following the latest FSA guidance, we are updating our peanut allergen signage across all UK stores. As you know, Five Guys uses <strong>100% refined peanut oil</strong> for all frying and offers <strong>complimentary roasted peanuts</strong> in-store.</p>
<p><strong>Updated Requirements:</strong></p>
<ul>
<li>New A3 allergen warning signs at ALL entrance points (front door, side door, delivery entrance)</li>
<li>Counter-top allergen cards at every till position</li>
<li>Peanut box areas must have floor-standing signage within 1 metre</li>
<li>Drive-through locations: allergen warning on menu board AND at collection window</li>
<li>Updated digital allergen matrix poster in crew area</li>
</ul>
<p><strong>Critical Reminders:</strong></p>
<ul>
<li>Customers MUST NOT take peanuts off-site (liability risk)</li>
<li>If a customer reports an allergic reaction, follow the Emergency Allergen Protocol immediately</li>
<li>All new crew members must complete peanut allergen e-learning within 48 hours of starting</li>
</ul>
<p>New signage kits are being delivered this week. Store managers: confirm installation within 3 days of receipt.</p>`,
    summary: 'Updated FSA-compliant peanut allergen signage requirements — new signs being delivered to all stores',
    imageUrl: 'https://images.unsplash.com/photo-1639070863891-a20035caf05a?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(6),
    expiresAtUtc: daysFromNow(120),
    createdAtUtc: daysAgo(8),
    createdByUserId: 21,
    createdByUserName: 'Sophie Turner',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 445,
    likeCount: 32,
    commentCount: 8,
    tags: [24, 5, 14],  // allergen-safety, compliance, important
  },
  {
    id: 14,
    title: 'Covent Garden Store: 10th Anniversary Celebration',
    content: `<p>This July marks <strong>10 years</strong> since Five Guys opened its first UK restaurant on Long Acre, Covent Garden — July 4th, 2013!</p>
<p>To celebrate, we're planning a week of activities at the FG001 store:</p>
<p><strong>Anniversary Week (July 1-7):</strong></p>
<ul>
<li>Free regular fries with any burger purchase (Mon-Fri)</li>
<li>Anniversary-edition Five Guys t-shirts for all Covent Garden crew</li>
<li>VIP customer event on July 4th — first 50 customers get a free meal</li>
<li>Social media campaign: #FiveGuysUK10Years — encourage customers to share their memories</li>
<li>Local press and food bloggers invited for July 4th opening</li>
</ul>
<p><strong>FG001 Team:</strong> Additional crew will be rostered for the anniversary week. Check the updated rota in the task management system. Let's make the Murrells proud!</p>`,
    summary: 'Celebrating 10 years of Five Guys in the UK — anniversary week activities at our Covent Garden flagship',
    imageUrl: 'https://images.unsplash.com/photo-1617201460038-6e5555a8a1f5?q=80&w=1288&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 11,
    storeNumbers: ['FG001'],
    isGlobal: false,
    isPinned: false,
    publishedAtUtc: daysAgo(7),
    expiresAtUtc: daysFromNow(45),
    createdAtUtc: daysAgo(9),
    createdByUserId: 20,
    createdByUserName: 'Ryan Mitchell',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 89,
    likeCount: 67,
    commentCount: 11,
    tags: [11, 3],  // event, promotion
  },
  {
    id: 15,
    title: 'Loaded Fries Launch: Cheese Sauce & Fry Sauce Training',
    content: `<p>Our new <strong>Loaded Fries</strong> are now live on the UK menu! Both Loaded Cajun and Loaded Salted fries come with our signature cheese sauce and fry sauce, plus customers can add up to 14 toppings.</p>
<p><strong>Assembly Standards:</strong></p>
<ul>
<li>Base: Regular portion of fries (Five Guys Style or Cajun)</li>
<li>Cheese sauce: 2oz ladle, drizzled in a Z-pattern across the fries</li>
<li>Fry sauce: 1oz squeeze bottle, dots around the edges</li>
<li>Toppings: placed ON TOP of sauces so they're visible to the customer</li>
<li>Served in the large fry cup with a fork</li>
</ul>
<p><strong>Cheese Sauce Handling:</strong></p>
<ul>
<li>Heated in the sauce warmer — check temperature every hour (must be above 63°C)</li>
<li>Discard after 4 hours in the warmer</li>
<li>Never mix new batch into old</li>
<li>Allergens: contains milk, soya</li>
</ul>
<p>This is a significant menu addition — expect high customer interest. Upsell from regular fries to loaded fries at point of order!</p>`,
    summary: 'Loaded Fries now on UK menu — cheese sauce and fry sauce assembly standards and handling procedures',
    imageUrl: 'https://images.unsplash.com/photo-1639744210631-209fce3e256c?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(12),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(14),
    createdByUserId: 22,
    createdByUserName: 'Dan Parker',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 378,
    likeCount: 94,
    commentCount: 19,
    tags: [22, 4, 21],  // fries, training, burgers-grill
  },
  {
    id: 16,
    title: 'Secret Shopper Results Q1: Burger Temperature & Extra Scoop Compliance',
    content: `<p>Q1 secret shopper results are in. Overall score: <strong>87/100</strong> — good, but we have room to improve.</p>
<p><strong>Highlights (scoring 90+):</strong></p>
<ul>
<li>Greeting and friendliness: 95/100 ⭐</li>
<li>Fry quality and freshness: 93/100 ⭐</li>
<li>Store cleanliness: 91/100</li>
</ul>
<p><strong>Areas for Improvement:</strong></p>
<ul>
<li>Burger internal temperature consistency: 82/100 — some patties under-rested, losing juices</li>
<li>Extra scoop compliance: 79/100 — not all crew giving the extra scoop consistently</li>
<li>Topping freshness at end of day: 80/100 — lettuce quality drops after 8pm</li>
</ul>
<p><strong>Action Items:</strong></p>
<ol>
<li>All grill operators: review the patty resting procedure (30 seconds after removing from grill)</li>
<li>Shift managers: audit extra scoop compliance during every shift</li>
<li>Evening crew: re-prep lettuce at 7pm using the hand-tear method</li>
</ol>
<p>Remember: the extra scoop isn't optional — it's what makes customers feel they got a great deal. It's already priced in. Give it generously!</p>`,
    summary: 'Q1 secret shopper results: 87/100 overall — action needed on burger temps and extra scoop compliance',
    imageUrl: 'https://plus.unsplash.com/premium_photo-1661544380643-830be2df7637?q=80&w=1308&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(15),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(16),
    createdByUserId: 21,
    createdByUserName: 'Sophie Turner',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 412,
    likeCount: 28,
    commentCount: 22,
    tags: [26, 25, 21],  // quality-control, fresh-toppings, burgers-grill
  },
];

const mockComments: NewsCommentDto[] = [
  { id: 1, newsItemId: 1, content: 'Excited for the new smoothies!', createdAtUtc: daysAgo(8), createdByUserId: 5, createdByUserName: 'Alex Brown', parentCommentId: null, likeCount: 12, isEdited: false },
  { id: 2, newsItemId: 1, content: 'When will the training materials be available?', createdAtUtc: daysAgo(8), createdByUserId: 6, createdByUserName: 'Jamie Wilson', parentCommentId: null, likeCount: 5, isEdited: false },
  { id: 3, newsItemId: 1, content: 'They are already in the Documents section under Training', createdAtUtc: daysAgo(8), createdByUserId: 1, createdByUserName: 'Sarah Johnson', parentCommentId: 2, likeCount: 8, isEdited: false },
  { id: 4, newsItemId: 3, content: 'Well deserved! Emma is amazing!', createdAtUtc: daysAgo(27), createdByUserId: 7, createdByUserName: 'Chris Lee', parentCommentId: null, likeCount: 25, isEdited: false },
  { id: 5, newsItemId: 3, content: 'Congratulations Emma! 🎉', createdAtUtc: daysAgo(27), createdByUserId: 8, createdByUserName: 'Pat Morgan', parentCommentId: null, likeCount: 18, isEdited: false },
];

const mockTags: NewsTagDto[] = [
  { id: 1, name: 'announcement', description: 'General announcements', color: '#3B82F6', isActive: true, usageCount: 5, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 2, name: 'menu', description: 'Menu updates and changes', color: '#10B981', isActive: true, usageCount: 8, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 3, name: 'promotion', description: 'Promotional events and offers', color: '#F59E0B', isActive: true, usageCount: 12, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 4, name: 'training', description: 'Training materials and updates', color: '#8B5CF6', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 5, name: 'compliance', description: 'Compliance and regulatory updates', color: '#EF4444', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 6, name: 'health-safety', description: 'Health and safety information', color: '#EC4899', isActive: true, usageCount: 7, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 7, name: 'recognition', description: 'Employee recognition and awards', color: '#14B8A6', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 8, name: 'maintenance', description: 'System maintenance notices', color: '#6B7280', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 9, name: 'holiday', description: 'Holiday schedules and information', color: '#F97316', isActive: true, usageCount: 6, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 10, name: 'policy', description: 'Policy updates and changes', color: '#64748B', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 11, name: 'event', description: 'Special events', color: '#A855F7', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 12, name: 'update', description: 'General updates', color: '#06B6D4', isActive: true, usageCount: 9, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 13, name: 'reminder', description: 'Reminders and deadlines', color: '#FBBF24', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 14, name: 'important', description: 'High priority items', color: '#DC2626', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  // Vapiano-specific tags
  { id: 15, name: 'fresh-pasta', description: 'Fresh pasta and pasta station updates', color: '#D4213D', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 16, name: 'italian-cuisine', description: 'Italian food and recipe updates', color: '#5C7A29', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 17, name: 'open-kitchen', description: 'Open kitchen standards and operations', color: '#B5192C', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 18, name: 'herb-garden', description: 'Herb garden and olive tree care', color: '#6E8F35', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 19, name: 'bar-drinks', description: 'Bar, cocktails, and drinks menu', color: '#E8856A', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 20, name: 'pizza-oven', description: 'Pizza oven and pizza station', color: '#C91D34', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  // Five Guys-specific tags
  { id: 21, name: 'burgers-grill', description: 'Burger and grill station updates', color: '#C92027', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 22, name: 'fries', description: 'Fries, fry station, and potato updates', color: '#F59E0B', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 23, name: 'milkshakes', description: 'Milkshake and mix-in updates', color: '#EC4899', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 24, name: 'allergen-safety', description: 'Allergen management and peanut safety', color: '#7C3AED', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 25, name: 'fresh-toppings', description: 'Toppings prep and freshness standards', color: '#059669', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 26, name: 'quality-control', description: 'Quality standards and secret shopper', color: '#0284C7', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
];

let nextNewsItemId = 17;
let nextTagId = 27;

/* ---- Mocked Client Class ---- */

export class NewsClientMocked implements INewsClient {
  async getNewsItems(filters?: {
    status?: NewsItemStatus;
    franchiseId?: number;
    storeNumber?: string;
    searchText?: string;
    isPinned?: boolean;
    includeExpired?: boolean;
  }): Promise<NewsItemDto[]> {
    let items = [...mockNewsItems];
    
    if (filters) {
      if (filters.status) {
        items = items.filter(i => getNewsItemStatus(i.publishedAtUtc, i.expiresAtUtc) === filters.status);
      }
      if (filters.franchiseId) {
        items = items.filter(i => i.franchiseId === filters.franchiseId);
      }
      if (filters.storeNumber) {
        items = items.filter(i => i.isGlobal || (i.storeNumbers && i.storeNumbers.includes(filters.storeNumber!)));
      }
      if (filters.searchText) {
        const text = filters.searchText.toLowerCase();
        items = items.filter(i => 
          i.title.toLowerCase().includes(text) || 
          i.summary.toLowerCase().includes(text) ||
          (i.tags && i.tags.some(tagId => {
            const tag = mockTags.find(t => t.id === tagId);
            return tag && tag.name.toLowerCase().includes(text);
          })),
        );
      }
      if (filters.isPinned !== undefined) {
        items = items.filter(i => i.isPinned === filters.isPinned);
      }
      if (!filters.includeExpired) {
        const now = new Date().toISOString();
        items = items.filter(i => !i.expiresAtUtc || i.expiresAtUtc > now);
      }
    }
    
    // Sort: pinned first, then by published date descending
    items.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      const dateA = a.publishedAtUtc || a.createdAtUtc;
      const dateB = b.publishedAtUtc || b.createdAtUtc;
      return dateB.localeCompare(dateA);
    });
    
    return Promise.resolve(items);
  }

  async getNewsItem(id: number): Promise<NewsItemDto | null> {
    const item = mockNewsItems.find(i => i.id === id);
    return Promise.resolve(item ? { ...item } : null);
  }

  async createNewsItem(dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const newItem: NewsItemDto = {
      id: nextNewsItemId++,
      ...dto,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'Current User',
      updatedAtUtc: null,
      updatedByUserId: null,
      updatedByUserName: null,
      viewCount: 0,
      likeCount: 0,
      commentCount: 0,
    };
    mockNewsItems.push(newItem);
    return Promise.resolve({ ...newItem });
  }

  async updateNewsItem(id: number, dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const index = mockNewsItems.findIndex(i => i.id === id);
    if (index === -1) throw new Error('News item not found');

    const currentItem = mockNewsItems[index];
    
    const updated: NewsItemDto = {
      ...currentItem,
      ...dto,
      publishedAtUtc: dto.publishedAtUtc,
      updatedAtUtc: new Date().toISOString(),
    };
    mockNewsItems[index] = updated;
    return Promise.resolve({ ...updated });
  }

  async deleteNewsItem(id: number): Promise<void> {
    mockNewsItems = mockNewsItems.filter(i => i.id !== id);
    return Promise.resolve();
  }

  async getComments(newsItemId: number): Promise<NewsCommentDto[]> {
    const comments = mockComments.filter(c => c.newsItemId === newsItemId);
    return Promise.resolve([...comments]);
  }

  async getEngagementSummary(newsItemId: number): Promise<NewsEngagementSummaryDto> {
    const item = mockNewsItems.find(i => i.id === newsItemId);
    if (!item) throw new Error('News item not found');
    
    return Promise.resolve({
      newsItemId: item.id,
      viewCount: item.viewCount,
      likeCount: item.likeCount,
      commentCount: item.commentCount,
      uniqueViewers: Math.floor(item.viewCount * 0.85),
      engagementRate: item.viewCount > 0 ? ((item.likeCount + item.commentCount) / item.viewCount) * 100 : 0,
    });
  }

  async deleteComment(commentId: number): Promise<void> {
    const index = mockComments.findIndex(c => c.id === commentId);
    if (index !== -1) {
      const newsItemId = mockComments[index].newsItemId;
      mockComments.splice(index, 1);
      // Update comment count
      const itemIndex = mockNewsItems.findIndex(i => i.id === newsItemId);
      if (itemIndex !== -1) {
        mockNewsItems[itemIndex].commentCount--;
      }
    }
    return Promise.resolve();
  }

  async recordView(newsItemId: number): Promise<void> {
    const index = mockNewsItems.findIndex(i => i.id === newsItemId);
    if (index !== -1) {
      mockNewsItems[index].viewCount++;
    }
    return Promise.resolve();
  }

  async toggleLike(newsItemId: number, liked: boolean): Promise<NewsItemDto> {
    const index = mockNewsItems.findIndex(i => i.id === newsItemId);
    if (index === -1) throw new Error('News item not found');
    
    if (liked) {
      mockNewsItems[index].likeCount += 1;
    } else {
      mockNewsItems[index].likeCount = Math.max(0, mockNewsItems[index].likeCount - 1);
    }
    return Promise.resolve({ ...mockNewsItems[index] });
  }

  async addComment(newsItemId: number, content: string): Promise<NewsCommentDto> {
    const nextCommentId = mockComments.length > 0 ? Math.max(...mockComments.map(c => c.id)) + 1 : 1;
    const newComment: NewsCommentDto = {
      id: nextCommentId,
      newsItemId,
      content,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'Current User',
      parentCommentId: null,
      likeCount: 0,
      isEdited: false,
    };
    mockComments.push(newComment);
    
    // Update comment count
    const itemIndex = mockNewsItems.findIndex(i => i.id === newsItemId);
    if (itemIndex !== -1) {
      mockNewsItems[itemIndex].commentCount++;
    }
    
    return Promise.resolve({ ...newComment });
  }

  /* ---- Tag CRUD Operations ---- */

  async getTags(filters?: { activeOnly?: boolean; searchText?: string }): Promise<NewsTagDto[]> {
    let tags = [...mockTags];

    if (filters) {
      if (filters.activeOnly) {
        tags = tags.filter(t => t.isActive);
      }
      if (filters.searchText) {
        const text = filters.searchText.toLowerCase();
        tags = tags.filter(t =>
          t.name.toLowerCase().includes(text) ||
          (t.description && t.description.toLowerCase().includes(text)),
        );
      }
    }

    // Sort alphabetically by name
    tags.sort((a, b) => a.name.localeCompare(b.name));

    return Promise.resolve(tags);
  }

  async getTag(id: number): Promise<NewsTagDto | null> {
    const tag = mockTags.find(t => t.id === id);
    return Promise.resolve(tag ? { ...tag } : null);
  }

  async createTag(dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    // Check for duplicate name
    const existing = mockTags.find(t => t.name.toLowerCase() === dto.name.toLowerCase());
    if (existing) {
      throw new Error(`Tag with name "${dto.name}" already exists`);
    }

    const newTag: NewsTagDto = {
      id: nextTagId++,
      name: dto.name.toLowerCase().trim(),
      description: dto.description || null,
      color: dto.color || null,
      isActive: dto.isActive,
      usageCount: 0,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: null,
    };
    mockTags.push(newTag);
    return Promise.resolve({ ...newTag });
  }

  async updateTag(id: number, dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    const index = mockTags.findIndex(t => t.id === id);
    if (index === -1) throw new Error('Tag not found');

    // Check for duplicate name (excluding current tag)
    const existing = mockTags.find(t => t.name.toLowerCase() === dto.name.toLowerCase() && t.id !== id);
    if (existing) {
      throw new Error(`Tag with name "${dto.name}" already exists`);
    }

    const updated: NewsTagDto = {
      ...mockTags[index],
      name: dto.name.toLowerCase().trim(),
      description: dto.description || null,
      color: dto.color || null,
      isActive: dto.isActive,
      updatedAtUtc: new Date().toISOString(),
    };
    mockTags[index] = updated;
    return Promise.resolve({ ...updated });
  }

  async deleteTag(id: number): Promise<void> {
    const index = mockTags.findIndex(t => t.id === id);
    if (index === -1) throw new Error('Tag not found');

    mockTags[index] = {
      ...mockTags[index],
      isActive: false,
      updatedAtUtc: new Date().toISOString(),
    };
    return Promise.resolve();
  }
}
